/**
 * CONSOLA DE PARTIDO — las cinco pantallas de la Fase D en una sola ruta.
 *
 * ## POR QUÉ ES UNA RUTA DEL PORTAL Y NO UNA APP NUEVA
 *
 * La tentación era hacer una app aparte (build propio, bundle mínimo, tema
 * propio). No se hizo, y por razones concretas:
 *
 *  - el SSE con reconexión y backoff, el `ApiClient` con sus errores
 *    traducidos, el i18n y el manejo de sesión **ya existen y están probados**;
 *    una app nueva los copiaría y, tarde o temprano, los dos se despegarían;
 *  - el QR de la mesa tiene que apuntar a algo que exista en la red del
 *    estadio, y el portal ya está desplegado y servido;
 *  - un build más es un artefacto más que versionar, publicar y explicar.
 *
 * Lo que sí es propio es el LENGUAJE VISUAL: la ruta se dibuja a pantalla
 * completa, sin el header del portal, con su hoja de estilos (`.bench`) y
 * pensada para mirarse de reojo, a un metro, con luz de estadio. La densidad
 * del portal —tablas, tipografía chica, números tabulares— es correcta para un
 * dirigente con una laptop y es exactamente lo contrario de lo que sirve en un
 * banco.
 *
 * ## LAS CINCO VISTAS SON UNA PROYECCIÓN, NO CINCO PANTALLAS
 *
 * Banco, mesa, árbitro, espejo del supervisor y pantalla de estadio comen del
 * MISMO `GET /bench`. Lo que cambia es qué se dibuja y qué se puede apretar,
 * nunca qué se sabe del partido — que es lo único que no puede pasar cuando hay
 * siete pantallas del mismo partido en la misma cancha. Y las N instancias de
 * árbitro que pide §5.4 salen gratis: es la misma vista, sin botones.
 *
 * ## ACREDITACIÓN
 *
 * Se entra con el QR que emite la mesa (§4.3). El token viaja **en el hash**,
 * no en la query: los fragmentos no se mandan al servidor, así que el secreto
 * de la credencial no queda en ningún access log ni en un `Referer`. Apenas se
 * canjea, se limpia de la URL.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import { Card } from '../../components/States';
import type { ApiClient, Identity } from '../../lib/api-client';
import type { PortalSession } from '../../lib/auth';
import {
  canRequest,
  canResolve,
  ownQueue,
  pendingCount,
  viewForRole,
  type BenchIntent,
  type BenchSnapshot,
  type BenchView,
} from '../../lib/bench';
import { benchHash, navigate, type BenchViewSegment } from '../../lib/router';
import { defaultKeyValueStore } from '../../lib/settings';
import { CourtPanel, LinkBanner, NumbersPanel, RalliesPanel, ScoreHero, TeamChips } from './BenchPanels';
import { LineupPanel, QueuePanel, RequestPanel } from './BenchQueue';
import { useBenchConsole, useMatchNumbers } from './use-bench-console';

const PREF_KEY = 'scout.portal.bench.prefs';

interface BenchPrefs {
  /** Cancha girada 180°: depende de qué lado está sentado el cuerpo técnico. */
  flipped: boolean;
  /** Contraste alto: la tablet a pleno reflector del estadio. */
  glare: boolean;
}

const DEFAULT_PREFS: BenchPrefs = { flipped: false, glare: false };

function loadPrefs(): BenchPrefs {
  try {
    const raw = defaultKeyValueStore().getItem(PREF_KEY);
    if (raw === null) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<BenchPrefs>;
    return {
      flipped: parsed.flipped === true,
      glare: parsed.glare === true,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: BenchPrefs): void {
  try {
    defaultKeyValueStore().setItem(PREF_KEY, JSON.stringify(prefs));
  } catch {
    /* modo privado: las preferencias duran lo que la pestaña */
  }
}

export interface BenchPageProps {
  client: ApiClient;
  matchId: string;
  /** Vista forzada por la URL; sin esto la elige el rol de la credencial. */
  view?: BenchViewSegment;
  /** Query del hash: trae el QR (`org`, `c`, `t`) la primera vez. */
  query: Record<string, string>;
  identity: Identity | null;
  /** Guarda la sesión que devuelve el canje del QR. */
  onSession: (session: PortalSession) => void;
  /** URL del VAR de ESTA cancha (config del navegador, ADR-017). */
  replayUrl: string;
}

export function BenchPage({
  client,
  matchId,
  view,
  query,
  identity,
  onSession,
  replayUrl,
}: BenchPageProps): JSX.Element {
  const t = useT();
  const [prefs, setPrefs] = useState<BenchPrefs>(loadPrefs);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const credentialId = query['c'] ?? '';
  const token = query['t'] ?? '';
  const org = query['org'] ?? '';
  const needsExchange = credentialId !== '' && token !== '' && org !== '';

  // --- Canje del QR ---------------------------------------------------------
  useEffect(() => {
    if (!needsExchange) return;
    let cancelled = false;
    client
      .matchLogin({ org, matchId, credentialId, token })
      .then((result) => {
        if (cancelled) return;
        onSession({
          token: result.token,
          user: {
            id: credentialId,
            username: `cred:${credentialId}`,
            name: result.match.holderName,
            role: 'viewer',
          },
          since: new Date().toISOString(),
        });
        // El secreto sale de la URL apenas se canjeó: la tablet queda en la
        // mesa de al lado toda la tarde y el historial del navegador no es
        // lugar para un token de 32 bytes.
        navigate(benchHash(matchId, view));
      })
      .catch((err: unknown) => {
        if (!cancelled) setAccessError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [client, matchId, credentialId, token, org, needsExchange, onSession, view]);

  const consola = useBenchConsole(client, matchId);
  const numbers = useMatchNumbers(client, matchId);
  // Se desestructuran las dos funciones ESTABLES (`useCallback` del hook) en vez
  // de meter `consola` entero en las dependencias: el hook devuelve un objeto
  // nuevo en cada render, así que un `useCallback([consola])` no memoriza nada.
  const { applyIntent, refresh } = consola;

  const permissions = identity?.permissions ?? [];
  const role = identity?.match?.role ?? null;
  const resolved: BenchView = view ?? viewForRole(role);
  const team: TeamSide = identity?.match?.team ?? 'home';

  const setPref = useCallback((patch: Partial<BenchPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      savePrefs(next);
      return next;
    });
  }, []);

  const resolve = useCallback(
    (intent: BenchIntent, decision: 'ACCEPT' | 'REJECT') => {
      setBusyId(intent.id);
      client
        .resolveIntent(matchId, intent.id, decision)
        .then((result) => {
          applyIntent(result.intent);
          refresh();
        })
        .catch(() => {
          // El server ya decidió; el poll trae el estado real en dos segundos.
          refresh();
        })
        .finally(() => setBusyId(null));
    },
    [client, matchId, applyIntent, refresh],
  );

  const cancel = useCallback(
    (intent: BenchIntent) => {
      setBusyId(intent.id);
      client
        .cancelIntent(matchId, intent.id)
        .then((result) => applyIntent(result.intent))
        .catch(() => refresh())
        .finally(() => setBusyId(null));
    },
    [client, matchId, applyIntent, refresh],
  );

  const jump = useMemo(() => {
    if (replayUrl.trim() === '') return null;
    return (videoNs: number): void => {
      // El clip lo entrega el VAR de la cancha, directo al navegador (ADR-017):
      // el API no proxea video. Acá se abre el punto de entrada del rally.
      const url = `${replayUrl.replace(/\/+$/, '')}/api/seek?ns=${videoNs}`;
      const g = globalThis as { open?: (u: string, target?: string) => unknown };
      g.open?.(url, '_blank');
    };
  }, [replayUrl]);

  const { snapshot } = consola;

  if (accessError !== null) {
    return (
      <div className="bench">
        <Card title={t('bench.access.title')}>
          <p>{t('bench.access.failed')}</p>
          <p className="hint">{accessError}</p>
        </Card>
      </div>
    );
  }

  if (snapshot === null) {
    return (
      <div className="bench">
        <Card title={t('bench.access.title')}>
          <p>{consola.error ?? t('bench.access.loading')}</p>
          {consola.error !== null ? (
            <button type="button" className="bench-btn" onClick={consola.refresh}>
              {t('bench.link.retry')}
            </button>
          ) : null}
        </Card>
      </div>
    );
  }

  const shellClass = `bench view-${resolved}${prefs.glare ? ' glare' : ''}`;

  // La pantalla de estadio es el único caso sin barra ni paneles: es un
  // tablero, no una consola. Cualquier cosa alrededor del marcador es ruido a
  // treinta metros.
  if (resolved === 'tablero') {
    return (
      <div className={shellClass}>
        <ScoreHero snapshot={snapshot} huge />
        <LinkBanner
          link={consola.link}
          lastSyncAt={consola.lastSyncAt}
          now={consola.now}
          onRetry={consola.refresh}
        />
      </div>
    );
  }

  const rival: TeamSide = team === 'home' ? 'away' : 'home';
  // La formación por tablet es OFICIAL (regla 5.2.3.1): se ofrece mientras el
  // set todavía no arrancó o el documento está antes del partido.
  const lineupOpen =
    canRequest(resolved, permissions) &&
    (snapshot.phase === 'idle' || snapshot.phase === 'configured' || snapshot.phase === 'between_sets');
  const pendientes = pendingCount(consola.intents);
  const cola = resolved === 'banco' ? ownQueue(consola.intents, team) : consola.intents;

  return (
    <div className={shellClass}>
      <BenchBar
        snapshot={snapshot}
        view={resolved}
        pending={pendientes}
        holder={identity?.match?.holderName ?? identity?.name ?? ''}
        prefs={prefs}
        onPref={setPref}
      />

      <LinkBanner
        link={consola.link}
        lastSyncAt={consola.lastSyncAt}
        now={consola.now}
        onRetry={consola.refresh}
      />

      <ScoreHero snapshot={snapshot} />
      <div className="bench-chips-row">
        <TeamChips team="home" state={snapshot.teams.home} name={snapshot.meta.home.name} />
        <TeamChips team="away" state={snapshot.teams.away} name={snapshot.meta.away.name} />
      </div>

      <div className="bench-grid">
        {/* El banco ve SU cancha grande; los demás ven las dos, porque su
            trabajo es comparar (el 2do árbitro controla las dos rotaciones). */}
        {resolved === 'banco' ? (
          <CourtPanel
            team={team}
            state={snapshot.teams[team]}
            name={snapshot.meta[team].name}
            meta={snapshot.meta[team]}
            serving={snapshot.servingTeam === team}
            flipped={prefs.flipped}
            onFlip={() => setPref({ flipped: !prefs.flipped })}
          />
        ) : (
          (['home', 'away'] as const).map((side) => (
            <CourtPanel
              key={side}
              team={side}
              state={snapshot.teams[side]}
              name={snapshot.meta[side].name}
              meta={snapshot.meta[side]}
              serving={snapshot.servingTeam === side}
              flipped={prefs.flipped}
              onFlip={side === 'home' ? () => setPref({ flipped: !prefs.flipped }) : undefined}
            />
          ))
        )}

        {canRequest(resolved, permissions) ? (
          <RequestPanel
            client={client}
            snapshot={snapshot}
            team={team}
            onIntent={applyIntent}
            onLineup={lineupOpen ? () => scrollToLineup() : undefined}
          />
        ) : null}

        <QueuePanel
          snapshot={snapshot}
          intents={cola}
          now={consola.now}
          onResolve={canResolve(resolved, permissions) ? resolve : null}
          onCancel={canRequest(resolved, permissions) ? cancel : null}
          busyId={busyId}
        />

        {/* El banco también ve la cancha del rival: contra quién juega la
            rotación propia es la mitad de la decisión de un cambio. */}
        {resolved === 'banco' ? (
          <CourtPanel
            team={rival}
            state={snapshot.teams[rival]}
            name={snapshot.meta[rival].name}
            meta={snapshot.meta[rival]}
            serving={snapshot.servingTeam === rival}
            flipped={false}
          />
        ) : null}

        {resolved === 'banco' || resolved === 'espejo' ? (
          <NumbersPanel report={numbers.report} unavailable={numbers.unavailable} meta={snapshot.meta} />
        ) : null}

        <RalliesPanel snapshot={snapshot} onJump={jump} />

        {/* La formación por tablet es OFICIAL (regla 5.2.3.1): se ofrece
            mientras el set todavía no arrancó o el documento está antes del
            partido. Durante el juego lo que corresponde es la sustitución. */}
        {lineupOpen ? (
          <LineupPanel
            client={client}
            snapshot={snapshot}
            team={team}
            onIntent={applyIntent}
          />
        ) : null}
      </div>
    </div>
  );
}

/** El botón "Formación" de los pedidos lleva al panel donde se arma. */
function scrollToLineup(): void {
  const g = globalThis as { document?: Document };
  g.document?.getElementById('bench-lineup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * `#/bench` sin partido: la pantalla que explica cómo se entra.
 *
 * No hay un formulario para elegir partido a propósito. A la consola se entra
 * con la credencial que emite la mesa, y la credencial YA dice de qué partido
 * es: ofrecer un selector sería sugerir que se puede mirar cualquier otro.
 */
export function BenchEntryCard(): JSX.Element {
  const t = useT();
  return (
    <div className="bench">
      <Card title={t('bench.access.title')}>
        <p>{t('bench.access.explain')}</p>
        <p className="hint">{t('bench.access.hint')}</p>
      </Card>
    </div>
  );
}

/** Barra superior: quién sos, qué vista es, y los dos interruptores de cancha. */
function BenchBar({
  snapshot,
  view,
  pending,
  holder,
  prefs,
  onPref,
}: {
  snapshot: BenchSnapshot;
  view: BenchView;
  pending: number;
  holder: string;
  prefs: BenchPrefs;
  onPref: (patch: Partial<BenchPrefs>) => void;
}): JSX.Element {
  const t = useT();
  return (
    <header className="bench-bar">
      <div className="bench-bar-title">
        <strong>{snapshot.name}</strong>
        <span className="bench-bar-view">{t(`bench.views.${view}` as 'bench.views.banco')}</span>
        {holder !== '' ? <span className="bench-bar-holder">{holder}</span> : null}
      </div>
      <div className="bench-bar-actions">
        {pending > 0 ? (
          <span className="bench-badge">{t('bench.queue.pending', { n: pending })}</span>
        ) : null}
        <button
          type="button"
          className="bench-btn ghost"
          aria-pressed={prefs.glare}
          onClick={() => onPref({ glare: !prefs.glare })}
        >
          {prefs.glare ? t('bench.prefs.glareOff') : t('bench.prefs.glareOn')}
        </button>
      </div>
    </header>
  );
}
